import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { billingAPI } from "../services/api/billing";

// Below this many credits we warn the user before they run out entirely.
export const LOW_CREDIT_FLOOR = 100;

// Growth-only feature keys — must match Plan::FEATURE_KEYS gating on the backend.
// A key not listed here is treated as available on every plan.
const GROWTH_FEATURES = ["prompt_optimizer", "discovery", "schedules", "alerts", "sso"];

// Shape returned when we cannot load the summary. Never hard-block the UI on
// a billing hiccup — but that reasoning only holds for the CREDIT fields.
// The PLAN fields must stay unknown (null/false) rather than asserting the
// lowest tier: FeatureRoute treats isPayg as "show the Growth upsell", and
// asserting isPayg here would tell an already-Growth workspace to upgrade to
// the plan it's already on. An unknown plan falls through to the silent
// dashboard redirect instead.
const SAFE_DEFAULT = {
  loading: false,
  planCode: null,
  isPayg: false,
  isGrowth: false,
  entitlements: {},
  legacyFeatures: {},
  credits: 0,
  outOfCredits: false,
  lowCredits: false,
  // No welcome-credit deadline is safer than a fabricated one — a billing
  // hiccup must never tell the user credits expire on some made-up date.
  welcomeCredits: null,
};

/**
 * useEntitlements — plan / credit-aware gating for the pay-as-you-go pricing model.
 *
 * Reads the current workspace's billing summary and derives:
 *   - planCode / isPayg / isGrowth  — which plan the workspace is on
 *   - credits / outOfCredits / lowCredits — credit-balance state
 *   - welcomeCredits — { expires_at, remaining, expired } | null — the 14-day
 *     welcome-grant expiry, null when the account never had one
 *   - entitlements / legacyFeatures — raw maps from the subscription
 *   - can(featureKey) — whether a feature is available on this plan
 *   - reload() — re-fetch the summary
 */
export default function useEntitlements() {
  const { currentWorkspace } = useAuth();
  const [state, setState] = useState({ ...SAFE_DEFAULT, loading: true });

  const load = useCallback(async () => {
    try {
      const s = await billingAPI.getBillingSummary(currentWorkspace?.id);
      // billing_controller#summary puts the plan code as a FLAT string on the
      // subscription sub-object (`subscription.plan_code`), not `plan.code`.
      const planCode = s?.subscription?.plan_code || "pay_as_you_go";
      const credits = Number(s?.total_balance ?? 0);
      setState({
        loading: false,
        planCode,
        isPayg: planCode === "pay_as_you_go",
        isGrowth: planCode === "growth",
        entitlements: s?.subscription?.entitlements || {},
        legacyFeatures: s?.subscription?.legacy_features || {},
        credits,
        outOfCredits: credits <= 0,
        lowCredits: credits > 0 && credits < LOW_CREDIT_FLOOR,
        // null for accounts that never had a welcome grant (e.g. admin-provisioned).
        welcomeCredits: s?.welcome_credits || null,
      });
    } catch {
      setState({ ...SAFE_DEFAULT });
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const can = useCallback(
    (key) => {
      // Optimistic while loading — don't flash a gate before we know the plan.
      if (state.loading) return true;
      // An explicit legacy-feature grant/denial always wins.
      if (state.legacyFeatures && key in state.legacyFeatures) {
        return !!state.legacyFeatures[key];
      }
      // Otherwise: available unless it's a Growth-only feature.
      return !GROWTH_FEATURES.includes(key);
    },
    [state]
  );

  return { ...state, can, reload: load };
}
